// ============================================================
// PlatformEventTypesPage.js
// Route /platform/event-types — Platform Management → Event Types
// (CareTrack reference catalogue).
//
// Table: Code, Title, Applicable Setting, Sort Order, Status.
// Add / Edit form in a Modal. Toggle active via PATCH isActive.
// Schema has no Description column on EventTypes; the form
// surfaces only the fields the table stores.
// ============================================================

import React, { useCallback, useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import DataTable from '../components/DataTable';
import Switch from '../components/Switch';
import Modal from '../components/Modal';
import Field from '../components/Field';
import Badge from '../components/Badge';
import { toast } from '../components/Toaster';
import {
  listPlatformEventTypes, createPlatformEventType, patchPlatformEventType,
} from '../api/adminPlatform';
import { translateError } from '../utils/errorMessages';
import './PlatformPages.css';

const SETTING_LABELS = {
  both:         'Both',
  residential:  'Residential',
  domiciliary:  'Domiciliary',
};

const BLANK_FORM = { code: '', title: '', applicableSetting: 'both', sortOrder: 0, typeSpecificDataSchema: '' };

// Item 39: the per-type structured capture fields. Edited as JSON here
// deliberately — this is an A2Z-IT-only operator surface used rarely, and
// a bespoke field-builder UI is a lot of machinery for that. The SERVER
// validates the shape (shared/caretrack-field-schema.js) and returns a
// named error, so a malformed schema is refused with a reason rather than
// stored. Left blank = no structured fields = the generic notes box.
const SCHEMA_PLACEHOLDER = `[
  { "key": "injury_location", "label": "Injury location", "type": "text" },
  { "key": "body_part", "label": "Body part", "type": "select", "options": ["Head", "Arm", "Leg"] }
]`;

export default function PlatformEventTypesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pendingId, setPendingId] = useState(null);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(BLANK_FORM);
  const [formError, setFormError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await listPlatformEventTypes();
      setItems(res.items || []);
    } catch (err) { setError(translateError(err)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchData(); }, [fetchData]);

  function openAdd() {
    setEditingId(null);
    setForm(BLANK_FORM);
    setFormError(null);
    setIsFormOpen(true);
  }
  function openEdit(row) {
    setEditingId(row.eventTypeId);
    setForm({
      code: row.code,
      title: row.title,
      applicableSetting: row.applicableSetting,
      sortOrder: row.sortOrder,
      typeSpecificDataSchema: row.typeSpecificDataSchema
        ? JSON.stringify(row.typeSpecificDataSchema, null, 2)
        : '',
    });
    setFormError(null);
    setIsFormOpen(true);
  }
  function closeForm() {
    if (submitting) return;
    setIsFormOpen(false);
    setEditingId(null);
    setForm(BLANK_FORM);
  }

  async function submitForm(e) {
    e.preventDefault();
    setFormError(null); setSubmitting(true);
    try {
      if (editingId) {
        // Code is immutable — strip from PATCH body.
        const { code: _omit, ...patch } = form;
        // Blank textarea = clear the schema (null), not an empty string:
        // null is the "no structured fields" state the column means.
        const raw = (patch.typeSpecificDataSchema || '').trim();
        if (raw) {
          try { patch.typeSpecificDataSchema = JSON.parse(raw); }
          catch { setFormError('The field schema is not valid JSON.'); setSubmitting(false); return; }
        } else {
          patch.typeSpecificDataSchema = null;
        }
        await patchPlatformEventType(editingId, patch);
        toast.success('Event type updated');
      } else {
        // Create does not take a schema — configure it on the edit form
        // once the type exists, so there is ONE write path for it.
        const { typeSpecificDataSchema: _skip, ...createBody } = form;
        await createPlatformEventType(createBody);
        toast.success('Event type created');
      }
      setIsFormOpen(false);
      setEditingId(null);
      setForm(BLANK_FORM);
      await fetchData();
    } catch (err) {
      setFormError(translateError(err));
    } finally { setSubmitting(false); }
  }

  async function toggleActive(row, nextActive) {
    setPendingId(row.eventTypeId);
    try {
      const result = await patchPlatformEventType(row.eventTypeId, { isActive: nextActive });
      if (result && result.wasIdempotent) return;
      toast.success(nextActive ? 'Event type activated' : 'Event type deactivated');
      await fetchData();
    } catch (err) { toast.error(translateError(err)); }
    finally { setPendingId(null); }
  }

  const columns = [
    { key: 'code',    label: 'Code',    render: (r) => <code className="platform-page__code">{r.code}</code>, width: '180px' },
    { key: 'title',   label: 'Title',   render: (r) => r.title },
    { key: 'setting', label: 'Applicable Setting',
      render: (r) => <Badge variant="neutral">{SETTING_LABELS[r.applicableSetting] || r.applicableSetting}</Badge>,
      width: '160px' },
    { key: 'sortOrder', label: 'Sort Order', render: (r) => r.sortOrder, width: '100px', align: 'right' },
    { key: 'active', label: 'Status', width: '100px',
      render: (r) => (
        <Switch
          checked={r.isActive}
          onChange={(next) => toggleActive(r, next)}
          disabled={pendingId === r.eventTypeId}
          ariaLabel={`${r.code} active`}
        />
      ),
    },
    { key: 'edit', label: '', width: '80px',
      render: (r) => (
        <button type="button" className="platform-page__row-action" onClick={() => openEdit(r)}>
          Edit
        </button>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Event Types"
        subtitle="CareTrack platform reference. Used by all clients; per-client overrides live on each client's Platform Config tab."
        cta={(
          <button type="button" className="platform-page__cta" onClick={openAdd}>
            <Plus size={16} /> Add Event Type
          </button>
        )}
      />

      <DataTable
        data={items}
        columns={columns}
        rowKey={(r) => r.eventTypeId}
        loading={loading}
        error={error}
        onRetry={fetchData}
        totalCount={items.length}
        page={1}
        pageSize={items.length || 1}
        onPageChange={() => { /* no pagination */ }}
        emptyMessage="No event types defined."
      />

      <Modal
        isOpen={isFormOpen}
        onClose={closeForm}
        title={editingId ? 'Edit event type' : 'Add event type'}
        closeDisabled={submitting}
      >
        <form onSubmit={submitForm} className="platform-page__form">
          <Field label="Code" required help="Lower-snake-case, 3-50 chars. Immutable after creation.">
            <input
              type="text"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
              disabled={Boolean(editingId)}
              maxLength={50}
              required
              pattern="^[a-z0-9_]{3,50}$"
            />
          </Field>
          <Field label="Title" required>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              maxLength={200}
              required
            />
          </Field>
          <Field label="Applicable Setting" required>
            <select
              value={form.applicableSetting}
              onChange={(e) => setForm({ ...form, applicableSetting: e.target.value })}
            >
              <option value="both">Both</option>
              <option value="residential">Residential</option>
              <option value="domiciliary">Domiciliary</option>
            </select>
          </Field>
          <Field label="Sort Order">
            <input
              type="number"
              min={0}
              max={9999}
              value={form.sortOrder}
              onChange={(e) => setForm({ ...form, sortOrder: parseInt(e.target.value, 10) || 0 })}
            />
          </Field>
          {editingId && (
            <Field label="Structured capture fields (JSON)">
              <textarea
                rows={8}
                spellCheck={false}
                value={form.typeSpecificDataSchema}
                onChange={(e) => setForm({ ...form, typeSpecificDataSchema: e.target.value })}
                placeholder={SCHEMA_PLACEHOLDER}
              />
              <small>
                Leave blank for a single free-text box. Types: text, textarea, select, date,
                number. Fields cannot be made mandatory - a required field on an incident
                form risks the event not being logged at all.
              </small>
            </Field>
          )}
          {formError && <p className="platform-page__form-error" role="alert">{formError}</p>}
          <div className="platform-page__form-actions">
            <button type="button" onClick={closeForm} disabled={submitting} className="platform-page__btn-secondary">
              Cancel
            </button>
            <button type="submit" disabled={submitting} className="platform-page__btn-primary">
              {submitting ? 'Saving…' : (editingId ? 'Save' : 'Create')}
            </button>
          </div>
        </form>
      </Modal>
    </>
  );
}
